import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/i18n";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar as CalendarIcon } from "lucide-react";

export default function MyJournal() {
  const { t } = useI18n();
  const [entries, setEntries] = useState([]);
  const [groups, setGroups] = useState([]);
  const [groupId, setGroupId] = useState("all");
  const [dateFilter, setDateFilter] = useState("");

  useEffect(() => {
    (async () => {
      const [j, g] = await Promise.all([
        api.get("/journal"),
        api.get("/groups"),
      ]);
      setEntries(j.data);
      setGroups(g.data);
    })();
  }, []);

  const groupsMap = useMemo(() => Object.fromEntries(groups.map((g) => [g.id, g])), [groups]);
  const filtered = entries.filter((e) => {
    if (groupId !== "all" && e.group_id !== groupId) return false;
    if (dateFilter && e.date !== dateFilter) return false;
    return true;
  });

  return (
    <div>
      <PageHeader title={t("my_journal")} subtitle={t("my_journal_subtitle")} testId="my-journal-header" />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-5 max-w-2xl">
        <div>
          <Label className="text-xs">{t("group")}</Label>
          <Select value={groupId} onValueChange={setGroupId}>
            <SelectTrigger data-testid="my-journal-group-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("all")}</SelectItem>
              {groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">{t("date")}</Label>
          <Input
            type="date"
            data-testid="my-journal-date-filter"
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
          />
        </div>
      </div>

      <div className="space-y-3">
        {filtered.length === 0 && (
          <div className="bg-white border border-slate-200 rounded-lg p-10 text-center text-slate-500">
            {t("no_data")}
          </div>
        )}
        {filtered.map((e) => (
          <Card key={e.id} className="p-5" data-testid={`my-journal-entry-${e.id}`}>
            <div className="flex items-center gap-3 text-xs text-slate-500 mb-1 flex-wrap">
              <span className="flex items-center gap-1.5"><CalendarIcon className="h-3.5 w-3.5" /> {e.date}</span>
              <span className="text-slate-400">· {groupsMap[e.group_id]?.name || e.group_id}</span>
            </div>
            <h3 className="font-display text-lg font-semibold text-slate-900">{e.topic}</h3>
            <p className="text-sm text-slate-600 mt-2 whitespace-pre-wrap">{e.content}</p>
          </Card>
        ))}
      </div>
    </div>
  );
}
